import { bindActionCreators } from '@reduxjs/toolkit';
import pako from 'pako';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { translateActions } from '../../store/slices/translateSlice';
import { RootState } from '../../store/store';
import { GetTranslateApiUrl } from '../../utils/URLUtils';
import useUser from './useUser';

interface UseTranslateReturn {
    translate: (text: string, checkPro?: boolean) => Promise<string>;
    translateMultiple: (texts: string[], checkPro?: boolean) => Promise<string[]>;
    isLoading: boolean;
}

export const useTranslate = (): UseTranslateReturn => {
    const { t } = useTranslation();
    const { userInfo, isNonEnglishNativeLanguage } = useUser();
    const [isTranslating, setIsTranslating] = useState(false);
    const [isTranslatingMultiple, setIsTranslatingMultiple] = useState(false);
    const dispatch = useDispatch();
    const ac = bindActionCreators(translateActions, dispatch);
    const { translationCount, lastResetTime } = useSelector((state: RootState) => state.translate);

    // Check if 5 hours have passed since last reset
    useEffect(() => {
        if (userInfo && userInfo.Pro) return;
        const fiveHoursInMs = 5 * 60 * 60 * 1000;
        const timeSinceLastReset = Date.now() - lastResetTime;

        if (timeSinceLastReset >= fiveHoursInMs) {
            ac.resetTranslationCount();
        }
    }, [lastResetTime, userInfo, ac]);

    const toGZippedBase64 = useCallback((text: string): string => {
        const data = new TextEncoder().encode(text);
        const compressed = pako.gzip(data);
        return btoa(String.fromCharCode.apply(null, Array.from(compressed)));
    }, []);

    // Helper function to decode Base64 and decompress GZip
    const fromGZippedBase64 = useCallback((base64: string): string => {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        const decompressed = pako.ungzip(bytes);
        return new TextDecoder().decode(decompressed);
    }, []);

    const translate = useCallback(async (text: string, checkPro: boolean = true): Promise<string> => {
        if (!text) return '';
        if (!isNonEnglishNativeLanguage()) return '';

        // Check if user is not pro and has exceeded the limit
        if (checkPro && userInfo && !userInfo.Pro && translationCount >= 5) {
            return t('Get Pro');
        }

        try {
            setIsTranslating(true);
            const compressedText = toGZippedBase64(text);
            const encodedText = encodeURIComponent(compressedText);

            const url = `${GetTranslateApiUrl()}?to=${userInfo?.NativeLanguageCode}&from=${encodedText}`;

            const response = await fetch(url);
            const compressedResponse = await response.text();

            if (!compressedResponse) return '';

            // Decode HTML entities and decompress
            const decodedText = decodeURIComponent(compressedResponse);
            const translatedText = fromGZippedBase64(decodedText);

            // Increment translation count for non-pro users
            if (userInfo && !userInfo.Pro) {
                ac.incrementTranslationCount();
            }

            return translatedText;
        } finally {
            setIsTranslating(false);
        }
    }, [t, ac, userInfo, isNonEnglishNativeLanguage, toGZippedBase64, fromGZippedBase64, translationCount]);

    const translateMultiple = useCallback(async (texts: string[], checkPro: boolean = true): Promise<string[]> => {
        if (!isNonEnglishNativeLanguage()) return [];
        if (!texts.length) return [];


        try {
            setIsTranslatingMultiple(true);
            const results = await Promise.all(
                texts.map(text => translate(text, checkPro))
            );

            return results;
        } finally {
            setIsTranslatingMultiple(false);
        }
    }, [translate, isNonEnglishNativeLanguage]);


    return {
        translate,
        translateMultiple,
        isLoading: isTranslating || isTranslatingMultiple,
    };
};
